import NavDrawer from './NavDrawer';
import { useState, useEffect } from 'react';
import { supabase, fetchAllRows } from './supabaseClient';
import { loadPublishedSets } from './setSource';
import { SECTIONS, byNewest } from './setSections';
import { lightAccent } from './theme';

export default function CommunityChecklist() {
  // Sets load asynchronously from Supabase (same source as My Checklists / SetBrowser).
  // Previously hardcoded setList which drifted out of sync as new sets were
  // ingested via the pipeline.
  const [setList, setSetList] = useState([]);
  const [collectorCount, setCollectorCount] = useState(0);
  const [cardCounts, setCardCounts] = useState({});
  const [starredSets, setStarredSets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ck_starred_sets') || '[]'); } catch { return []; }
  });
  const [openSections, setOpenSections] = useState(() => {
    try {
      const starred = JSON.parse(localStorage.getItem('ck_starred_sets') || '[]');
      if (starred.length > 0) return { starred: true, 'chrome-black': false, 'chrome-sw': false, 'smugglers-outpost': false, galaxy: false };
      return { starred: false, 'chrome-black': true, 'chrome-sw': true, 'smugglers-outpost': true, galaxy: true };
    } catch { return { starred: true, 'chrome-black': false, 'chrome-sw': false, 'smugglers-outpost': false, galaxy: false }; }
  });

  // Load all published sets from Supabase (matches SetBrowser's source).
  useEffect(() => {
    let cancelled = false;
    loadPublishedSets().then(sets => {
      if (cancelled) return;
      setSetList(sets || []);
    }).catch(err => {
      console.error('CommunityChecklist: failed to load sets', err);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    supabase.from('public_profiles').select('id', { count: 'exact' }).eq('is_public', true)
      .then(({ count }) => setCollectorCount(count || 0));

    // Count unique cards per set across all public profiles
    supabase.from('public_profiles').select('id').eq('is_public', true).then(async ({ data: profiles }) => {
      if (!profiles?.length) return;
      // Paged: all public rows across every set far exceed the 1000-row cap.
      const rows = await fetchAllRows(() => supabase.from('collections')
        .select('card_key')
        .in('user_id', profiles.map(p => p.id))
        .order('user_id').order('card_key'))
        .catch(err => { console.error('CommunityChecklist: failed to load collections', err); return null; });
      if (!rows) return;
      const counts = {};
      rows.forEach(({ card_key }) => {
        // Extract set ID from card_key (first segment before __)
        const setId = card_key.split('__')[0];
        if (!counts[setId]) counts[setId] = new Set();
        // Only count base subset cards to match totalCards denominator
        const parts = card_key.split('__');
        const subSetId = parts[1] || '';
        if (parts.length === 3 && subSetId === 'base') {
          counts[setId].add(card_key);
        }
      });
      const result = {};
      Object.entries(counts).forEach(([k, v]) => result[k] = v.size);
      setCardCounts(result);
    });
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const toggleStar = (setId, e) => {
    e.stopPropagation();
    setStarredSets(prev => {
      const next = prev.includes(setId) ? prev.filter(id => id !== setId) : [...prev, setId];
      localStorage.setItem('ck_starred_sets', JSON.stringify(next));
      return next;
    });
  };

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const SetCard = ({ set }) => (
    <div style={s.card}
      onClick={() => navigate('/community/' + set.id)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = lightAccent(set.color); e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(var(--wash-rgb),0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
      <div style={{ ...s.cardAccent, background: set.color }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={s.cardYear}>{set.year}</div>
        <button
          onClick={(e) => toggleStar(set.id, e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: starredSets.includes(set.id) ? 'var(--gold)' : 'var(--border-2)', padding: '0', lineHeight: 1, transition: 'color 0.15s' }}
          title={starredSets.includes(set.id) ? 'Remove star' : 'Star this set'}>
          &#9733;
        </button>
      </div>
      <div style={s.cardName}>{set.shortName}</div>
      <div style={s.cardDesc}>{set.description}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid', borderTopColor: set.color + '33', marginTop: '8px' }}>
        <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)' }}>{set.totalCards ? set.totalCards + ' CARDS' : ''}</span>
        <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '1px', color: set.color }}>VIEW CHECKLIST →</span>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <NavDrawer />
      <div style={s.stars} />
      <header style={s.header}>
        <div style={s.headerInner}>
          <div>
            <div style={s.logoLine}>COLLECTIONKEEPER · COMMUNITY</div>
            <h1 style={s.title}><span style={s.titleAccent}>COMMUNITY</span> CHECKLISTS</h1>
            <div style={s.subtitle}>See what every public collector owns, wants, and has for trade — across every set.</div>
          </div>
        </div>
        <div style={s.headerLine} />
      </header>
      <div style={s.statsBar}>
        <div style={{ ...s.pill, borderColor: 'var(--cyan)', color: 'var(--cyan)' }}>{setList.length} SETS</div>
        <div style={{ ...s.pill, borderColor: 'var(--green)', color: 'var(--green)' }}>{collectorCount} PUBLIC COLLECTOR{collectorCount !== 1 ? 'S' : ''}</div>
      </div>
      <main style={s.main}>
        <div style={s.intro}>Select a set to view its community checklist — which cards collectors own, want, or have available for trade and sale.</div>

        {starredSets.length > 0 && (
          <div style={s.section}>
            <button style={{ ...s.accordionBtn, borderColor: 'var(--gold)' }} onClick={() => toggleSection('starred')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ ...s.sectionAccent, background: 'var(--gold)' }} />
                <span style={{ ...s.sectionTitle, color: 'var(--gold)' }}>&#9733; STARRED SETS</span>
                <span style={s.accordionCount}>{starredSets.length} SET{starredSets.length !== 1 ? 'S' : ''}</span>
              </div>
              <div style={{ ...s.chevron, transform: openSections.starred ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9662;</div>
            </button>
            {openSections.starred && (
              <div style={s.grid}>
                {setList.filter(set => starredSets.includes(set.id)).map(set => (
                  <SetCard key={set.id} set={set} />
                ))}
              </div>
            )}
          </div>
        )}

        {SECTIONS.map(section => {
          const sets = setList.filter(section.match).sort(byNewest);
          if (!sets.length) return null;
          const isOpen = openSections[section.id];
          return (
            <div key={section.id} style={s.section}>
              <button style={{ ...s.accordionBtn, borderColor: lightAccent(section.accent) + '33' }} onClick={() => toggleSection(section.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ ...s.sectionAccent, background: lightAccent(section.accent) }} />
                  <span style={{ ...s.sectionTitle, color: lightAccent(section.accent) }}>{section.label}</span>
                  <span style={s.accordionCount}>{sets.length} SET{sets.length !== 1 ? 'S' : ''}</span>
                </div>
                <div style={{ ...s.chevron, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9662;</div>
              </button>
              {isOpen && (
                <div style={s.grid}>
                  {sets.map(set => (
                    <SetCard key={set.id} set={set} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </main>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani', sans-serif", position: 'relative' },
  stars: { position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', pointerEvents: 'none', zIndex: 0, backgroundImage: 'radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.45) 0%, transparent 100%), radial-gradient(1px 1px at 60% 70%, rgba(255,255,255,0.3) 0%, transparent 100%)' },
  header: { position: 'relative', zIndex: 10, background: 'rgba(var(--bg-rgb),0.97)', padding: '24px 24px 0', borderBottom: '1px solid var(--border)' },
  headerInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '20px' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '5px', color: 'var(--brand-gold)', opacity: 0.8, marginBottom: '8px' },
  title: { fontFamily: "'Orbitron', monospace", fontSize: 'clamp(16px,3vw,28px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0, letterSpacing: '1px' },
  titleAccent: { color: 'var(--cyan)' },
  subtitle: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-2)', marginTop: '6px', lineHeight: 1.6 },
  headerLine: { height: '1px', background: 'linear-gradient(90deg, transparent, var(--cyan), var(--brand-gold), var(--cyan), transparent)' },
  statsBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', padding: '12px 20px', background: 'rgba(var(--bg-rgb),0.96)', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 10 },
  pill: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '5px 12px', borderRadius: '2px', border: '1px solid var(--border)', color: 'var(--muted-2)' },
  main: { position: 'relative', zIndex: 1, padding: '28px 24px 60px', maxWidth: '1200px', margin: '0 auto' },
  intro: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-2)', marginBottom: '28px', lineHeight: 1.7 },
  section: { marginBottom: '16px' },
  accordionBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(var(--pcard-rgb),0.6)', border: '1px solid var(--border)', borderRadius: '2px', padding: '12px 16px', cursor: 'pointer', marginBottom: '0' },
  sectionAccent: { width: '3px', height: '14px', borderRadius: '1px', flexShrink: 0 },
  sectionTitle: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '3px' },
  accordionCount: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted-2)', letterSpacing: '1px' },
  chevron: { color: 'var(--muted-2)', fontSize: '12px', transition: 'transform 0.2s', flexShrink: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px', padding: '10px 0 6px' },
  card: { position: 'relative', background: 'var(--panel)', border: '1px solid rgba(var(--wash-rgb),0.06)', borderRadius: '2px', padding: '20px', cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s', display: 'flex', flexDirection: 'column', gap: '6px', overflow: 'hidden' },
  cardAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px' },
  cardYear: { fontFamily: "'Orbitron', monospace", fontSize: '18px', fontWeight: 900, color: 'rgba(var(--wash-rgb),0.15)', letterSpacing: '2px' },
  cardName: { fontFamily: "'Orbitron', monospace", fontSize: '12px', fontWeight: 700, color: 'var(--text-strong)', letterSpacing: '1px', lineHeight: 1.3 },
  cardDesc: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted-2)', lineHeight: 1.5, flex: 1 },
  cardCta: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', paddingTop: '10px', borderTop: '1px solid', marginTop: '8px' },
};
